import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'

import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../util/analytics'
import { PAGE_PATHS } from '../config/pagePaths'
import { MESSAGE_AUDITS_UNAUTHORIZED } from '../content/tooltipMessages'
import { useAuthorized } from '../hooks/useAuthorization'
import { useMessage } from '../hooks/useMessage'
import { Company } from '../models/client/company'
import { User } from '../models/client/user'
import { color } from '../styles'
import { SPACING } from '../styles/mixins/constants'
import { text } from '../styles/mixins/text'
import { FAST_TRANSITION } from '../styles/mixins/transition'
import { insertTestingSelector } from '../util/testing'

import { Icon, IconKey } from './Icon'
import { Logo } from './Icon/SVG'
import { Spacer } from './Spacer'

const CollapseContainer = styled.div`
  position: absolute;
  top: 0;
  cursor: pointer;
`

const Container = styled.div`
  min-width: 241px;
  background-color: ${color.background.nav};
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`

const Head = styled.div`
  display: flex;
  height: 70px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  background-color: ${color.background.navAlt};
`

const LogoContainer = styled.div`
  svg path {
    fill: ${color.text.inverted};
  }
  svg {
    height: 47px;
    width: 148px;
  }
  cursor: pointer;
`

const LinkDirectory = styled.div`
  display: flex;
  flex-flow: column nowrap;
`

const DisabledWrapper = styled.div<{ disabled?: boolean }>`
  opacity: 0.5;
  cursor: not-allowed;
`

const LinkLabel = styled.span``

const LinkContainer = styled.div<{ isCurrentPath?: boolean; disabled?: boolean }>`
  ${text('title', 'inverted', 'text')}
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: ${SPACING[4]} ${SPACING[6]};
  flex: 1 1 auto;
  cursor: pointer;
  user-select: none;
  ${FAST_TRANSITION}
  &:hover {
    background-color: ${color.background.navAlt};
  }
  &:active {
    opacity: 0.5;
  }
  ${(p) =>
    p.isCurrentPath &&
    css`
      background-color: ${color.background.navAlt};
    `}

  ${(p) =>
    p.disabled
      ? css`
          background-color: ${color.background.nav};
        `
      : ''}
`

const SubLinkContainer = styled.div<{ isCurrentPath?: boolean; disabled?: boolean }>`
  ${text('title', 'inverted', 'text')}
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  font-size: 18px;
  padding: 16px 50px;
  flex: 1 1 auto;
  cursor: pointer;
  user-select: none;
  line-height: 24px;
  ${FAST_TRANSITION}
  &:hover {
    background-color: ${color.background.navAlt};
  }
  &:active {
    opacity: 0.5;
  }
  ${(p) =>
    p.isCurrentPath &&
    css`
      background-color: ${color.background.navAlt};
    `}

  ${(p) =>
    p.disabled
      ? css`
          background-color: ${color.background.nav};
        `
      : ''}
`

const Footer = styled.div``

const UserSection = styled.div`
  background-color: ${color.background.navAlt};
  padding: ${SPACING[4]};
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`

const UserName = styled.div`
  ${text('title', 'inverted', 'text')}
`

const Status = styled.div`
  ${text('paragraph', 'secondary', 'text')}
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${color.status.active};
`

const LogoutButton = styled.div`
  cursor: pointer;
  ${FAST_TRANSITION}
  user-select: none;
  &:active {
    opacity: 50%;
  }
`

type Props = {
  user: User | null
  company: Company | null
  devtoolsEnabled?: boolean
  onLogout: () => void
  onDevtoolToggle?: () => void
}

export const NavbarContents = ({
  company,
  user,
  devtoolsEnabled,
  onLogout,
  onDevtoolToggle,
}: Props): JSX.Element => {
  const navigate = useNavigate()
  const isAuditsAuthorized = useAuthorized('audits')
  const isCheckinAuthorized = useAuthorized('checkins')
  const isCompanyYrDisabled = company?.settings?.disableYr || false
  const isYardReportDisabled = user?.meta?.disableYr
  const isISREnabled = company?.settings?.isrEnabled
  // const isAppointmentsAuthorized = useAuthorized('appointments')
  const messenger = useMessage()
  const { pathname } = useLocation()

  const links: Array<{
    label: string
    path: string
    iconKey: IconKey
    subLinks?: Array<any>
    eventName?: string
  }> = [
    // {
    //   label: 'Overview',
    //   path: PAGE_PATHS.overview,
    //   iconKey: 'Insights',
    // },
    {
      label: 'Gate Feed',
      path: PAGE_PATHS.gateFeed,
      iconKey: 'GateFeed' as IconKey,
      eventName: 'GateFeedView',
    },
    !isCompanyYrDisabled && {
      label: 'Yard Inventory',
      path: isYardReportDisabled ? PAGE_PATHS.reports : PAGE_PATHS.yardInventory,
      iconKey: 'Reports' as IconKey,
      eventName: 'YardInventoryView',
    },
    // {
    //   label: 'Alerts',
    //   path: PAGE_PATHS.alertsEvents,
    //   iconKey: 'Alerts',
    // },
    // {
    //     label: 'Audits',
    //     path: PAGE_PATHS.audits,
    //     iconKey: 'Audits',
    // },
    isCheckinAuthorized && {
      label: 'Check Ins',
      path: PAGE_PATHS.checkIn,
      iconKey: 'Checkin' as IconKey,
      subLinks: [
        {
          label: 'History',
          path: PAGE_PATHS.checkInHistory,
          iconKey: 'CheckinHistory' as IconKey,
        },
      ],
    },
    isISREnabled && {
      label: 'ISR Report',
      path: PAGE_PATHS.reportsISR2305,
      iconKey: 'Table' as IconKey,
      eventName: 'ISRReportView',
    },
    {
      label: 'Analytics',
      path: PAGE_PATHS.analytics,
      iconKey: 'Insights' as IconKey,
      eventName: 'AnalyticsView',
    },
    // {
    //   label: 'Appointments',
    //   path: PAGE_PATHS.appointments,
    //   iconKey: 'Appointments' as IconKey,
    // },
    // {
    //   label: 'Maintenance',
    //   path: PAGE_PATHS.maintenance,
    //   iconKey: 'Maintenance',
    // },
  ].filter(Boolean)

  return (
    <>
      <LinkDirectory>
        {links.map((link) => {
          const actionUnauthorized = link.path === '/audits' && !isAuditsAuthorized
          const ComponentLink = (
            <LinkDirectory key={`${link.path}-main`}>
              <LinkContainer
                {...insertTestingSelector('NavLink', link.path)}
                isCurrentPath={link.path === pathname}
                key={link.path}
                onClick={() => {
                  if (actionUnauthorized) {
                    messenger.danger(MESSAGE_AUDITS_UNAUTHORIZED)
                    return
                  }
                  if (link.eventName) {
                    analytics.track(AnalyticsEvent[link.eventName], AnalyticsEventTrigger.click)
                  }
                  navigate(link.path)
                }}
              >
                <Icon size={5} iconKey={link.iconKey} color="inverted" />
                <Spacer size={2} />
                <LinkLabel>{link.label}</LinkLabel>
              </LinkContainer>
              {link.subLinks?.map((sub) => {
                const SubComponentLink = (
                  <SubLinkContainer
                    {...insertTestingSelector('NavLink', link.path)}
                    isCurrentPath={sub.path === pathname}
                    key={sub.path}
                    onClick={() => {
                      navigate(sub.path)
                    }}
                  >
                    <Icon size={3} iconKey={sub.iconKey} color="inverted" />
                    <Spacer size={1} />
                    <LinkLabel>{sub.label}</LinkLabel>
                  </SubLinkContainer>
                )
                return SubComponentLink
              })}
            </LinkDirectory>
          )
          if (actionUnauthorized) {
            return <DisabledWrapper key={link.path}>{ComponentLink}</DisabledWrapper>
          }
          return ComponentLink
        })}
      </LinkDirectory>
      <Spacer size="fill" vertical />
      <Footer>
        {devtoolsEnabled ? (
          <LinkContainer onClick={onDevtoolToggle}>
            <Icon size={5} iconKey="Tool" color="inverted" />
            <Spacer size={2} />
            <LinkLabel>Devtools</LinkLabel>
          </LinkContainer>
        ) : (
          <div />
        )}

        {/*
                    HIDE: for now until Intercom integration solved
                    <LinkContainer
                        {...insertTestingSelector('NavChat')}
                        onClick={() => {
                            chatClient.show()
                        }}
                        style={{
                            display: 'none'
                        }}
                        >
                        <Icon size={5} iconKey="Message" color="inverted" />
                        <Spacer size={2} />
                        <LinkLabel>Support</LinkLabel>
                    </LinkContainer>
                */}

        <LinkContainer
          {...insertTestingSelector('NavLink', PAGE_PATHS.settings)}
          onClick={() => navigate(PAGE_PATHS.settings)}
        >
          <Icon size={5} iconKey="Settings" color="inverted" />
          <Spacer size={2} />
          <LinkLabel>Settings</LinkLabel>
        </LinkContainer>
        <LinkContainer
          {...insertTestingSelector('NavLink', PAGE_PATHS.help)}
          onClick={() => window.open(PAGE_PATHS.help, '_blank')}
        >
          <Icon size={5} iconKey="QuestionCircleOutlined" color="inverted" />
          <Spacer size={2} />
          <LinkLabel>Help</LinkLabel>
        </LinkContainer>
        <Spacer vertical size={2} />
        <UserSection>
          <UserName>
            {user?.name || 'LOADING'}
            <Status>
              <StatusDot />
              <Spacer size={2} />
              Logged In
            </Status>
          </UserName>
          <Spacer size="fill" />
          <LogoutButton onClick={onLogout}>
            <Icon iconKey="LogOut" color="danger" size={5} />
          </LogoutButton>
        </UserSection>
      </Footer>
    </>
  )
}

export const Navbar = (props: Props): JSX.Element => {
  const navigate = useNavigate()
  const { onLogout, user, company, devtoolsEnabled, onDevtoolToggle } = props
  const [isCollapsed, setIsCollapsed] = useState(false)
  return (
    <div>
      {!isCollapsed && (
        <Container>
          <Head>
            <LogoContainer
              onClick={() => navigate(PAGE_PATHS.gateFeed)}
              {...insertTestingSelector('NavLink', PAGE_PATHS.gateFeed)}
            >
              <Logo />
            </LogoContainer>
          </Head>
          <Spacer vertical size={2} />
          <NavbarContents
            onLogout={onLogout}
            user={user}
            company={company}
            devtoolsEnabled={devtoolsEnabled}
            onDevtoolToggle={onDevtoolToggle}
          />
        </Container>
      )}
      <CollapseContainer>
        {!isCollapsed && (
          <Icon
            iconKey="InArrow"
            color="inverted"
            size={5}
            style={{
              position: 'absolute',
              left: 214,
              top: 2,
            }}
            onClick={() => setIsCollapsed(true)}
          />
        )}
        {isCollapsed && (
          <Icon iconKey="OutArrow" color="primary" size={5} onClick={() => setIsCollapsed(false)} />
        )}
      </CollapseContainer>
    </div>
  )
}
